/**
 * PremiumStorePage — Full port of premium-store.page
 * Handles: product listing, purchase, VIP codes, restore, debug
 */
import { useState, useCallback } from 'react';
import { useMonetization } from '../services/monetization';

const PremiumStorePage = () => {
  const { products, premiumFeatures, isPremium, hasRemovedAds, purchaseProduct, restorePurchases, purchases, applyVipCode } = useMonetization();
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [vipInput, setVipInput] = useState('');
  const [showVip, setShowVip] = useState(false);
  const [message, setMessage] = useState('');

  const isProductPurchased = useCallback((pid) => {
    return purchases.some(p => p.productId === pid);
  }, [purchases]);

  const handlePurchase = async (productId) => {
    setPurchasing(true); setMessage('');
    try {
      await purchaseProduct(productId);
      setMessage('✅ வாங்குதல் வெற்றி! Premium feature activated.');
    } catch (err) {
      setMessage('❌ ' + (err.message || 'Purchase failed'));
    } finally { setPurchasing(false); }
  };

  const handleRestore = async () => {
    setRestoring(true); setMessage('');
    try {
      await restorePurchases();
      setMessage('✅ Purchases restored successfully.');
    } catch (err) {
      setMessage('❌ Restore failed: ' + err.message);
    } finally { setRestoring(false); }
  };

  const handleVip = () => {
    if (applyVipCode(vipInput.trim())) {
      setMessage('✅ VIP code accepted! All premium features unlocked.');
      setShowVip(false);
    } else {
      setMessage('❌ Invalid VIP code. Please check and try again.');
    }
  };

  const hasAllFeatures = premiumFeatures.muhurtham && premiumFeatures.jathagam && premiumFeatures.panchangam && premiumFeatures.noAds;

  return (
    <div style={{ padding: '16px', backgroundColor: '#f0f2f5', minHeight: '80vh' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#090b72', borderRadius: '12px', marginBottom: '16px' }}>
        <h1 style={{ color: 'white', fontSize: '1.3rem', margin: 0 }}>Premium Store</h1>
        <p style={{ color: '#f0d48a', fontSize: '0.85rem', margin: '4px 0 0' }}>v7.0</p>
      </div>

      {/* Message */}
      {message && <div style={{ padding: '10px', margin: '0 0 12px', backgroundColor: message.startsWith('✅') ? '#e8f5e9' : '#fbe9e7', borderRadius: '8px', fontSize: '0.9rem', textAlign: 'center' }}>{message}</div>}

      {/* Premium Features Status */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>🏆 Premium Features</h2>
        <FeatureRow label="முஹூர்த்தம்" desc="நல்லநாள், சிறப்பு முஹூர்த்தம், 28-சுபம்" active={premiumFeatures.muhurtham} />
        <FeatureRow label="ஜாதகம்" desc="ஜாதகம், பொருத்தம், லக்னம்" active={premiumFeatures.jathagam} />
        <FeatureRow label="பஞ்சாங்கம்" desc="10yr archive, Next 2yrs, Oonams PDF" active={premiumFeatures.panchangam} />
        <FeatureRow label="Ad-Free" desc={hasRemovedAds ? 'Ads Removed' : 'Ads Enabled'} active={hasRemovedAds} />
      </div>

      {/* Products */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>{hasAllFeatures ? '🏆 All Features Unlocked!' : 'Upgrade to Premium'}</h2>
        {!hasAllFeatures && <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 12px' }}>Purchase individual features or get all at once</p>}
        {hasAllFeatures && <p style={{ color: '#2e7d32', fontSize: '0.9rem', fontWeight: 600 }}>Thank you for your support! 🙏</p>}

        {products.map(product => (
          <div key={product.identifier} style={{ border: '1px solid #e0e0e0', borderRadius: '10px', padding: '12px', marginBottom: '10px', backgroundColor: isProductPurchased(product.identifier) ? '#e8f5e9' : 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333' }}>{product.title}</h3>
              {isProductPurchased(product.identifier) && <span style={{ backgroundColor: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700 }}>✓ PURCHASED</span>}
              {!isProductPurchased(product.identifier) && product.identifier === 'premium_all' && <span style={{ backgroundColor: '#ff9800', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700 }}>POPULAR</span>}
            </div>
            <p style={{ margin: '4px 0 8px', fontSize: '0.8rem', color: '#666' }}>{product.description}</p>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#090b72' }}>{product.price} <span style={{ fontSize: '0.7rem', color: '#888' }}>/ yr</span></span>
              {isProductPurchased(product.identifier) ? (
                <button disabled style={{ padding: '8px 16px', backgroundColor: '#4caf50', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700 }}>✓ Active</button>
              ) : (
                <button onClick={() => handlePurchase(product.identifier)} disabled={purchasing}
                  style={{ padding: '8px 16px', backgroundColor: '#090b72', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', opacity: purchasing ? 0.6 : 1 }}>
                  {purchasing ? 'Processing...' : '🛒 Purchase'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* VIP / Patron Code */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>🎁 Redeem VIP / Patron</h2>
        <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 12px' }}>Enter your VIP / patron code to unlock all premium features</p>
        {showVip ? (
          <div>
            <input type="text" value={vipInput} onChange={e => setVipInput(e.target.value)} placeholder="Enter VIP Code..."
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '2px solid #d4a845', fontSize: '1rem', marginBottom: '8px', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={handleVip} style={{ flex: 1, padding: '10px', backgroundColor: '#d4a845', color: '#0a0e27', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: 'pointer' }}>✅ Apply</button>
              <button onClick={() => setShowVip(false)} style={{ padding: '10px 16px', backgroundColor: '#eee', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowVip(true)} style={{ width: '100%', padding: '12px', backgroundColor: 'transparent', border: '2px solid #d4a845', color: '#d4a845', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '1rem' }}>
            🔑 Enter VIP / Patron Code
          </button>
        )}
        <p style={{ fontSize: '0.8rem', color: '#888', marginTop: '8px' }}>
          Have a special VIP code? Enter it above to unlock all premium features instantly.
          <a href="mailto:svknvs@gmail.com?subject=Requesting%20for%20patron%20member&body=Name:%0APhone:%0AReason:%0A" style={{ color: '#d4a845', fontWeight: 500, display: 'block', marginTop: '4px' }}>
            Request a patron code →
          </a>
        </p>
      </div>

      {/* Support */}
      <div style={cardStyle}>
        <h2 style={cardTitleStyle}>🆘 Support</h2>
        <p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '12px' }}>Having trouble with purchases or premium access?</p>
        <button onClick={handleRestore} disabled={restoring} style={{ ...btnOutline, marginBottom: '8px' }}>
          {restoring ? '⏳ Restoring...' : '🔄 Restore Purchases'}
        </button>
        <button onClick={() => { localStorage.removeItem('paranthaman_premium_cache'); window.location.reload(); }} style={{ ...btnOutline, borderColor: '#ff9800', color: '#ff9800' }}>
          🗑️ Clear App Data & Re-Sync
        </button>
        <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '12px', textAlign: 'center' }}>App Version: 7.0</p>
      </div>
    </div>
  );
};

const FeatureRow = ({ label, desc, active }) => (
  <div style={{ display: 'flex', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
    <span style={{ fontSize: '1.2rem', marginRight: '10px' }}>{active ? '✅' : '⭕'}</span>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, color: '#333' }}>{label}</div>
      <div style={{ fontSize: '0.8rem', color: '#666' }}>{desc}</div>
    </div>
    {active && <span style={{ backgroundColor: '#4caf50', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '0.7rem', fontWeight: 700 }}>ACTIVE</span>}
  </div>
);

const cardStyle = { backgroundColor: 'white', borderRadius: '12px', padding: '16px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
const cardTitleStyle = { color: '#1b5e20', fontSize: '1.1rem', margin: '0 0 10px', borderBottom: '2px solid #ff9800', paddingBottom: '6px' };
const btnOutline = { width: '100%', padding: '12px', backgroundColor: 'transparent', border: '2px solid #090b72', color: '#090b72', borderRadius: '8px', fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' };

export default PremiumStorePage;
